// Whole-cook phase scheduling — the single source of truth for where each
// phase sits on the wall clock, shared byte-for-byte by the server anchor
// (`apps/functions/cookpit/projection.ts`) and the client Planner
// (`apps/web/src/routes/Planner.tsx`). Keeping one implementation here is
// what stops the two from drifting (they previously disagreed on phase
// order).
//
// Model (parallel prep): PreCook and PrepCook are genuinely concurrent
// workstreams a cook runs side by side (README §6.3), so they share a
// wall-clock start; LiveCook begins when the *longer* prep block finishes
// and ends exactly at the serve time. With `prepBlock = max(preCook,
// prepCook)` over whichever prep phases are present:
//
//   cookStart      = serve − (prepBlock + liveCook)
//   preCook  start = cookStart            end = cookStart + preCook
//   prepCook start = cookStart            end = cookStart + prepCook
//   liveCook start = serve − liveCook     end = serve
//
// This keeps the load-bearing invariant `liveCook.start === serve −
// liveCook.nominalDuration` intact, and a prep phase that finishes before
// the other simply idles until LiveCook (the task-driven transition,
// README §6.7.2). Absent phases are omitted; LiveCook is always required.
//
// Nothing here re-times or compresses authored tasks — it only anchors the
// fixed phase clocks to a serve instant (README principle #2).

import type { PhaseId } from './anchor.js';

// Parse a cookpit phase duration to whole seconds. Accepts both the
// clock-style `HH:MM:SS` used by `phase.nominalDuration` and the ISO-8601
// designator form (`PT1H30M`, `P1D`) used elsewhere. Throws on anything
// else so malformed data surfaces loudly rather than projecting silently
// wrong times — callers validate the file before anchoring. Mirrors the
// historical `parseIsoDurationSeconds` in the function projection, which
// now re-exports this implementation.
export function parsePhaseDurationSeconds(d: string): number {
  const clock = /^(\d{2,}):(\d{2}):(\d{2})$/.exec(d);
  if (clock) {
    const [, hh, mm, ss] = clock;
    return parseInt(hh!, 10) * 3600 + parseInt(mm!, 10) * 60 + parseInt(ss!, 10);
  }
  const iso = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(d);
  if (!iso) throw new Error(`Bad duration: ${d}`);
  const [, dd, hh, mm, ss] = iso;
  const days = dd ? parseInt(dd, 10) : 0;
  const hours = hh ? parseInt(hh, 10) : 0;
  const mins = mm ? parseInt(mm, 10) : 0;
  const secs = ss ? parseFloat(ss) : 0;
  return ((days * 24 + hours) * 60 + mins) * 60 + secs;
}

// Parse a cookpit `HH:MM:SS.lane` task time into a (seconds-from-phase,
// lane) pair. The lane suffix (e.g. A0, S1, M3, D2) rides along so callers
// that need it (lane chips, hotspot grouping) don't re-parse. Throws on a
// malformed string so bad data surfaces loudly — files are schema-validated
// before they reach here. Single implementation shared by the server
// projection (`apps/functions/cookpit/projection.ts`) and the client
// Preview projection so their per-task offsets can never drift.
export function parseTaskTimeSeconds(s: string): { seconds: number; lane: string } {
  const m = /^(\d{2}):(\d{2}):(\d{2})\.([A-Z]\d)$/.exec(s);
  if (!m) throw new Error(`Bad task.time: ${s}`);
  const [, hh, mm, ss, lane] = m;
  const seconds = parseInt(hh!, 10) * 3600 + parseInt(mm!, 10) * 60 + parseInt(ss!, 10);
  return { seconds, lane: lane! };
}

// Minimal structural view of the cookpit block this calculator needs.
// Both the generated cookpit type and the server's internal projection
// shape satisfy it, so callers pass their existing object unchanged.
export interface PhaseDurations {
  preCook?: { nominalDuration: string } | null;
  prepCook?: { nominalDuration: string } | null;
  liveCook: { nominalDuration: string };
}

// One phase's wall-clock window, in epoch ms.
export interface PhaseWindow {
  phase: PhaseId;
  startMs: number;
  endMs: number;
  durationSeconds: number;
}

// The whole cook, anchored to a serve instant.
export interface WholeCookPlan {
  serveMs: number;
  // Earliest moment any phase begins — the cook's "start cooking at".
  cookStartMs: number;
  liveStartMs: number;
  // Wall-clock span from cookStart to serve, in seconds. Under the
  // parallel model this is `max(preCook, prepCook) + liveCook`, NOT the
  // sum of all three.
  totalSeconds: number;
  // Present phases in display order: preCook, prepCook, liveCook. The two
  // prep phases share a start, so their relative order is cosmetic.
  phases: PhaseWindow[];
}

// Pure: anchor every present phase to `serveMs` under the parallel-prep
// model. `serveMs` is epoch milliseconds; the caller owns time-zone and
// "now" concerns (feasibility is inherently present-relative and lives at
// the call site, not here).
export function computeWholeCookPlan(cookpit: PhaseDurations, serveMs: number): WholeCookPlan {
  const liveSec = parsePhaseDurationSeconds(cookpit.liveCook.nominalDuration);
  const preSec = cookpit.preCook ? parsePhaseDurationSeconds(cookpit.preCook.nominalDuration) : 0;
  const prepSec = cookpit.prepCook
    ? parsePhaseDurationSeconds(cookpit.prepCook.nominalDuration)
    : 0;

  const prepBlockSec = Math.max(preSec, prepSec);
  const liveStartMs = serveMs - liveSec * 1000;
  const cookStartMs = liveStartMs - prepBlockSec * 1000;

  const phases: PhaseWindow[] = [];
  if (cookpit.preCook) {
    phases.push({
      phase: 'preCook',
      startMs: cookStartMs,
      endMs: cookStartMs + preSec * 1000,
      durationSeconds: preSec,
    });
  }
  if (cookpit.prepCook) {
    phases.push({
      phase: 'prepCook',
      startMs: cookStartMs,
      endMs: cookStartMs + prepSec * 1000,
      durationSeconds: prepSec,
    });
  }
  phases.push({
    phase: 'liveCook',
    startMs: liveStartMs,
    endMs: serveMs,
    durationSeconds: liveSec,
  });

  return {
    serveMs,
    cookStartMs,
    liveStartMs,
    totalSeconds: prepBlockSec + liveSec,
    phases,
  };
}
